import React from 'react'
import Navigation from '../components/Navigation'
import Footer from '../components/Footer'

const PrivacyPolicy = () => {
  return (
    <div className="w-full min-h-screen bg-white">
      <Navigation />
      
      <main className="relative overflow-hidden">
        {/* Subtle background texture */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(28,35,41,0.05) 1.1px, transparent 0)',
            backgroundSize: '22px 22px',
            maskImage:
              'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 100%)',
          }}
          aria-hidden="true"
        />
        
        {/* Green glow accent */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(0,216,118,0.08),transparent_50%)]" />

        <div className="relative mx-auto max-w-4xl px-5 py-12 sm:px-8 sm:py-16 lg:px-12 lg:py-20">
          {/* Header */}
          <div className="mb-10 sm:mb-12">
            <h1 className="text-[32px] font-semibold leading-[1.12] tracking-[-1px] text-[#1c2329] sm:text-[40px] sm:leading-[1.1] sm:tracking-[-1.1px] md:text-[48px] md:leading-[1.08] md:tracking-[-1.2px]">
              Privacy Policy
            </h1>
            <p className="mt-3 text-[15px] leading-[1.6] tracking-[-0.32px] text-[#656f68] sm:text-[16px] sm:tracking-[-0.4px]">
              Last Updated: December 4th, 2024
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8 sm:space-y-10 text-[#1c2329]">
            {/* Introduction */}
            <section>
              <h2 className="text-[24px] font-semibold leading-[1.3] tracking-[-0.6px] text-[#1c2329] sm:text-[28px] sm:leading-[1.25] mb-4">
                Introduction
              </h2>
              <div className="space-y-4 text-[15px] leading-[1.7] tracking-[-0.32px] text-[#4b5650] sm:text-[16px] sm:leading-[1.75] sm:tracking-[-0.4px]">
                <p>
                  When you use our services, you're trusting us with your information. We understand this is a big responsibility and work hard to protect your information and put you in control.
                </p>
                <p>
                  This Privacy Policy is meant to help you understand what information we collect, why we collect it, and what you can do to protect your information.
                </p>
              </div>
            </section>

            {/* Key Terms */}
            <section>
              <h2 className="text-[24px] font-semibold leading-[1.3] tracking-[-0.6px] text-[#1c2329] sm:text-[28px] sm:leading-[1.25] mb-4">
                Key Terms
              </h2>
              <ul className="space-y-3 text-[15px] leading-[1.7] tracking-[-0.32px] text-[#4b5650] sm:text-[16px] sm:leading-[1.75] sm:tracking-[-0.4px]">
                <li className="flex gap-3">
                  <span className="text-[#00c86b] mt-1.5">•</span>
                  <span>
                    <strong className="text-[#1c2329] font-semibold">Automazing, LLC</strong> ("Notifyer Systems", "we", "us", or "our") is the company that collects and processes Personal Data for the purposes described in this Policy
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#00c86b] mt-1.5">•</span>
                  <span>
                    <strong className="text-[#1c2329] font-semibold">Personal Data</strong> is any information relating to an identified or identifiable natural person ("Data Subject")
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#00c86b] mt-1.5">•</span>
                  <span>
                    <strong className="text-[#1c2329] font-semibold">Customer or Client</strong> is a legal business entity with whom Notifyer Systems has an agreement to provide the Services
                  </span>
                </li>
              </ul>
            </section>

            {/* About Notifyer Systems */}
            <section>
              <h2 className="text-[24px] font-semibold leading-[1.3] tracking-[-0.6px] text-[#1c2329] sm:text-[28px] sm:leading-[1.25] mb-4">
                About Notifyer Systems
              </h2>
              <div className="space-y-4 text-[15px] leading-[1.7] tracking-[-0.32px] text-[#4b5650] sm:text-[16px] sm:leading-[1.75] sm:tracking-[-0.4px]">
                <p>
                  Notifyer Systems is a notification and communication platform that enables businesses to send automated notifications, alerts, and messages to their customers through the WhatsApp Business Platform. We specialize in integrating with other business workflow software services and integrate with the major automation systems, such as Make.com, allowing you to easily plug WhatsApp into your existing business workflow. We achieve this in two ways:
                </p>
                <ol className="list-decimal list-inside space-y-3 ml-2">
                  <li>
                    Through our API, which allows direct integration of WhatsApp messaging capabilities into companies' existing technology stacks and business systems
                  </li>
                  <li>
                    Through our pre-built modules on popular automation platforms (such as Make.com), enabling businesses to easily incorporate WhatsApp messaging into their automated workflows without complex coding
                  </li>
                </ol>
                <p>
                  Using the WhatsApp Cloud API, our platform acts as a technical bridge between businesses' existing systems and WhatsApp's messaging infrastructure. We handle the complex technical aspects of WhatsApp API integration while ensuring full compliance with WhatsApp's business policies and data protection standards.
                </p>
                <p>
                  You can get our private Notifyer Systems Make.com App on the marketplace. Using the WhatsApp Cloud API, our platform helps businesses send and manage their customer communication efficiently and securely. We facilitate the delivery of messages through the official WhatsApp Business API, ensuring compliance with WhatsApp's business policies and data protection standards.
                </p>
              </div>
            </section>

            {/* Data Collection & Use */}
            <section>
              <h2 className="text-[24px] font-semibold leading-[1.3] tracking-[-0.6px] text-[#1c2329] sm:text-[28px] sm:leading-[1.25] mb-4">
                Data Collection & Use
              </h2>
              <div className="space-y-4 text-[15px] leading-[1.7] tracking-[-0.32px] text-[#4b5650] sm:text-[16px] sm:leading-[1.75] sm:tracking-[-0.4px]">
                <p>
                  Notifyer Systems collects information, including Personal Data, for:
                </p>
                <ul className="space-y-3 ml-2">
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Providing and managing WhatsApp API integration services</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Facilitating connections between business systems and WhatsApp</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Supporting automation platform integrations</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Processing messages through the WhatsApp Business Platform</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Internal business purposes</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Understanding and improving our integration services</span>
                  </li>
                </ul>
                <p className="mt-4">
                  <strong className="text-[#1c2329] font-semibold">Required Information:</strong> We collect user's name, email address and phone number for service delivery and identification purposes. For WhatsApp message delivery, we process phone numbers and message content in accordance with Meta's WhatsApp Business Platform policies.
                </p>
              </div>
            </section>

            {/* Third-Party Integrations */}
            <section>
              <h2 className="text-[24px] font-semibold leading-[1.3] tracking-[-0.6px] text-[#1c2329] sm:text-[28px] sm:leading-[1.25] mb-4">
                Third-Party Integrations
              </h2>
              <div className="space-y-4 text-[15px] leading-[1.7] tracking-[-0.32px] text-[#4b5650] sm:text-[16px] sm:leading-[1.75] sm:tracking-[-0.4px]">
                <p>
                  Notifyer Systems collects information, including Personal Data, for:
                </p>
                <ul className="space-y-3 ml-2">
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Our service integrates with both business systems and automation platforms</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Data sharing with these platforms is governed by their respective privacy policies</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Users should review the privacy policies of any integrated services they use</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>We maintain data protection standards in accordance with Meta's requirements for technology providers</span>
                  </li>
                </ul>
                <p className="mt-4 italic text-[#656f68]">
                  Note: Our services are not directed to children under 16.
                </p>
              </div>
            </section>

            {/* Service Management */}
            <section>
              <h2 className="text-[24px] font-semibold leading-[1.3] tracking-[-0.6px] text-[#1c2329] sm:text-[28px] sm:leading-[1.25] mb-4">
                Service Management
              </h2>
              <ul className="space-y-3 text-[15px] leading-[1.7] tracking-[-0.32px] text-[#4b5650] sm:text-[16px] sm:leading-[1.75] sm:tracking-[-0.4px] ml-2">
                <li className="flex gap-3">
                  <span className="text-[#00c86b] mt-1.5">•</span>
                  <span>We may access and process Personal Data on behalf of our Customers</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#00c86b] mt-1.5">•</span>
                  <span>Customers control how their data is collected, used, and stored</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#00c86b] mt-1.5">•</span>
                  <span>For questions about Customer data usage, please contact them directly</span>
                </li>
              </ul>
            </section>

            {/* Security & Storage */}
            <section>
              <h2 className="text-[24px] font-semibold leading-[1.3] tracking-[-0.6px] text-[#1c2329] sm:text-[28px] sm:leading-[1.25] mb-4">
                Security & Storage
              </h2>
              <ul className="space-y-3 text-[15px] leading-[1.7] tracking-[-0.32px] text-[#4b5650] sm:text-[16px] sm:leading-[1.75] sm:tracking-[-0.4px] ml-2">
                <li className="flex gap-3">
                  <span className="text-[#00c86b] mt-1.5">•</span>
                  <span>Data is stored in secure cloud servers in compliance with Meta's data protection requirements</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#00c86b] mt-1.5">•</span>
                  <span>All personally identifiable information is encrypted</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#00c86b] mt-1.5">•</span>
                  <span>
                    Account deletion requests: <a href="mailto:team@whatsable.app" className="text-[#00c86b] hover:text-[#00a85a] underline">team@whatsable.app</a>
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#00c86b] mt-1.5">•</span>
                  <span>Data is deleted upon service termination unless otherwise requested</span>
                </li>
              </ul>
            </section>

            {/* Cookie Usage */}
            <section>
              <h2 className="text-[24px] font-semibold leading-[1.3] tracking-[-0.6px] text-[#1c2329] sm:text-[28px] sm:leading-[1.25] mb-4">
                Cookie Usage
              </h2>
              <div className="space-y-4 text-[15px] leading-[1.7] tracking-[-0.32px] text-[#4b5650] sm:text-[16px] sm:leading-[1.75] sm:tracking-[-0.4px]">
                <p>
                  We use session-based and persistent cookies for:
                </p>
                <ul className="space-y-3 ml-2">
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Account authentication</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>User preferences</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Usage tracking</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#00c86b] mt-1.5">•</span>
                    <span>Service improvement</span>
                  </li>
                </ul>
                <p className="mt-4 italic text-[#656f68]">
                  Note: We do not currently respond to Do Not Track signals.
                </p>
              </div>
            </section>

            {/* Policy Updates */}
            <section>
              <h2 className="text-[24px] font-semibold leading-[1.3] tracking-[-0.6px] text-[#1c2329] sm:text-[28px] sm:leading-[1.25] mb-4">
                Policy Updates
              </h2>
              <p className="text-[15px] leading-[1.7] tracking-[-0.32px] text-[#4b5650] sm:text-[16px] sm:leading-[1.75] sm:tracking-[-0.4px]">
                We may update this policy periodically. Continued service use after changes indicates acceptance of updates.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default PrivacyPolicy

